import clsx from 'clsx';
import { useAtom, useSetAtom } from 'jotai';
import { useParams } from 'react-router-dom';
import { useLocalStorageState } from 'ahooks';
import { type CSSProperties, type MouseEvent, memo, useEffect, useMemo, useState } from 'react';

import { useTag } from '@/hooks/rxdb-models';
import { useSearch } from '@/hooks/use-search';
import { useDocuMenu } from '@/hooks/use-docu-menu';
import { useSearchSort } from '@/hooks/use-search-sort';
import { useItemActions } from '@/hooks/use-item-actions';
import { useElementResize } from '@flintmint/web-shared/hooks/use-element-resize';

import { ListItem } from '@/components/list-item';
import { AssignTag } from '@/components/assign-tag';
import { TagHeader } from '@/components/tag-header';
import { Tab } from '@flintmint/web-shared/components/tab';
import { Icon } from '@flintmint/web-shared/components/icon';
import { MiniActionBar } from '@/components/mini-action-bar';
import { Tabs } from '@flintmint/web-shared/components/tabs';
import { DocumentPreview } from '@/components/document-preview';
import { Search } from '@flintmint/web-shared/components/search';
import { Button } from '@flintmint/web-shared/components/button';
import { NoData } from '@flintmint/web-shared/components/no-data';
import { Divider } from '@flintmint/web-shared/components/divider';
import { SortMenu } from '@flintmint/web-shared/components/sort-menu';
import { OverlaySidebar } from '@flintmint/web-shared/components/overlay-sidebar';

import { taggableTypes, tagsTypeFilters } from '@/constants/search-filters';
import { getColumnsCount, getRangeSlice, toggleObject } from '@/utils/helper-utils';
import { emptyLayoutAtom, sidebarOverlayAtom, taggedDocsMultiSelectionsAtom } from '@/state';

import type { DocumentPreviewContext, DocumentWithType } from '@/constants/types';
import type { SearchResult } from '@/services/search-service/types';
import type { SortParams } from '@flintmint/web-shared/constants/types';
import type { SearchableDocument } from '@/services/search-service/orama-schema';

import styles from './tag-workspace.module.scss';

// TODO handle wrong tagId case
export const TagWorkspace = memo(() => {
  const { tagId } = useParams();

  const { data: tag } = useTag(tagId);

  const [search, setSearch] = useState('');
  const [typeId, setTypeId] = useState('all');
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [lastAppliedShiftBandIds, setLastAppliedShiftBandIds] = useState<string[]>([]);

  const setEmptyLayout = useSetAtom(emptyLayoutAtom);
  const [sidebarOverlay, setSidebarOverlay] = useAtom(sidebarOverlayAtom);
  const [taggedDocsMultiSelections, setTaggedDocsMultiSelections] = useAtom(taggedDocsMultiSelectionsAtom);

  const taggedDocsMultiSelectionsOn = Array.isArray(taggedDocsMultiSelections);

  const { onRemoveFromTag } = useItemActions();
  const clearLocalRangeSelection = () => {
    setAnchorId(null);
    setLastAppliedShiftBandIds([]);
  };

  const selectDocument = (doc: DocumentWithType) => {
    setTaggedDocsMultiSelections((arr) => toggleObject(arr || [], doc));
  };

  const getMenuData = useDocuMenu(['open', 'preview', 'duplicate', 'select', 'setShortcut', 'removeFromTag', 'delete'], {
    select: (item, doc) => {
      return {
        ...item,
        onClick: () => selectDocument(doc),
      };
    },
  });

  const [sort, setSort] = useLocalStorageState('tag-documents-sort', {
    defaultValue: {
      key: 'updatedAt',
      direction: 'desc',
    } as SortParams,
  });

  const { isSearching, menuSort, querySort, setSearchSort } = useSearchSort(search, sort ?? null);

  const type = tagsTypeFilters.find((el) => el.id === typeId);
  const searchableTypes = type && typeId !== 'all' ? [type.id] : taggableTypes;

  const { data, isLoading } = useSearch(
    {
      term: search,
      isUserSearch: true,
      where: {
        tagIds: [tagId!],
        types: searchableTypes,
      },
      sort: querySort,
    },
    !!tag
  );

  const { stats: filterStats } = useSearch({
    term: search,
    where: {
      tagIds: [tagId!],
      types: taggableTypes,
    },
    includeStats: true,
  });

  const filterOptions = useMemo(() => {
    return tagsTypeFilters
      .map((filter) => {
        if (filter.id === 'all') {
          return {
            ...filter,
            count: filterStats?.total || 0,
          };
        }
        return {
          ...filter,
          count: filterStats?.[filter.id as keyof typeof filterStats] || 0,
        };
      })
      .filter((filter) => !!filter.count);
  }, [filterStats]);

  useEffect(() => {
    const currentFilterExists = filterOptions.some((filter) => filter.id === typeId);
    if (!currentFilterExists && typeId !== 'all') {
      setTypeId('all');
    }
  }, [filterOptions, typeId]);

  const { ref, clientWidth } = useElementResize([tagId, tag, !!data.length]);

  const closeSidebarOverlay = () => setSidebarOverlay(null);

  useEffect(() => {
    setEmptyLayout(!tagId);
    setSearch('');
    setTypeId('all');
    closeSidebarOverlay();
    setTaggedDocsMultiSelections(null);
    clearLocalRangeSelection();
  }, [tagId]);

  if (!tag) {
    return;
  }

  const onListItemClick = (event: MouseEvent, el: SearchResult) => {
    const selectedDocument = el.document;
    const selectedDocumentId = selectedDocument.id;
    const isCmdClick = event.metaKey || event.ctrlKey;
    const isShiftClick = event.shiftKey;

    if (isShiftClick) {
      const anchorInList = taggedDocsMultiSelectionsOn && !!anchorId && data.some((doc) => doc.document.id === anchorId);

      if (!anchorInList) {
        if (taggedDocsMultiSelectionsOn) {
          const currentSelections = taggedDocsMultiSelections ?? [];
          const isAlreadySelected = currentSelections.some((doc) => doc.id === selectedDocumentId);
          setTaggedDocsMultiSelections(isAlreadySelected ? currentSelections : [...currentSelections, selectedDocument]);
        } else {
          setTaggedDocsMultiSelections([selectedDocument]);
        }
        setAnchorId(selectedDocumentId);
        setLastAppliedShiftBandIds([selectedDocumentId]);
        return;
      }

      const range = getRangeSlice(
        data.map((doc) => doc.document),
        anchorId,
        selectedDocumentId
      );

      if (!range) return;

      setTaggedDocsMultiSelections((prev) => {
        const filtered = (prev ?? []).filter((doc) => !lastAppliedShiftBandIds.includes(doc.id));
        const rangeIds = new Set(range.map((doc) => doc.id));
        const keep = filtered.filter((doc) => !rangeIds.has(doc.id));
        return [...keep, ...range];
      });
      setLastAppliedShiftBandIds(range.map((doc) => doc.id));
      return;
    }

    if (isCmdClick || taggedDocsMultiSelectionsOn) {
      setLastAppliedShiftBandIds([]);

      const currentSelections = taggedDocsMultiSelections ?? [];
      const nextSelections = toggleObject(currentSelections, selectedDocument);
      const isSelectedAfterToggle = nextSelections.some((doc) => doc.id === selectedDocumentId);

      setTaggedDocsMultiSelections(nextSelections.length ? nextSelections : null);
      setAnchorId((prevAnchor) => {
        if (isSelectedAfterToggle) {
          return selectedDocumentId;
        }

        if (prevAnchor === selectedDocumentId) {
          return null;
        }

        return prevAnchor;
      });
      return;
    }

    clearLocalRangeSelection();
    setSidebarOverlay({
      type: 'document-preview',
      context: {
        type: selectedDocument.type,
        docId: el.id,
      },
    });
  };

  // numDocs can be transiently negative while a device's tag and document streams are out of
  // step (see tag-handler.ts), so "has tagged docs" is a > 0 test, not truthiness.
  const hasTaggedDocs = tag.numDocs > 0;

  const filtersNoResult = hasTaggedDocs && !!search && !data.length;

  const renderEmpty = (
    <NoData
      className={clsx(styles.tagsWorkspaceEmpty, {
        [styles.hasFilters]: filtersNoResult,
      })}
      icon={<Icon type={filtersNoResult ? 'search-01-bulk' : 'tag-02-bulk'} size={filtersNoResult ? 60 : 108} multiColor />}
      description={
        filtersNoResult
          ? 'No Items were found for your request'
          : `Nothing here yet!\nAssign the tag to any item so you can quickly filter out!`
      }
      actionRenderer={() => {
        if (filtersNoResult) {
          return (
            <Button color="dark" appearance="outline" onClick={() => setSearch('')}>
              Clear Search
            </Button>
          );
        }

        return <AssignTag tagId={tag?.id} taggedDocs={data} />;
      }}
    />
  );

  const columns = getColumnsCount(clientWidth, 280, 450);

  return (
    <>
      <div className={styles.tagsStickyHeader}>
        <TagHeader tag={tag} taggedDocs={data} />
        {hasTaggedDocs && (
          <ul className={styles.tagFilters}>
            <li
              className={clsx({
                [styles.filtersNoResult]: filtersNoResult,
              })}
            >
              <Tabs value={typeId} appearance="simple" onChange={setTypeId} className={styles.libraryFilters}>
                {filterOptions.map(({ name, count, id }) => (
                  <Tab id={id} key={id} count={count} name={name} role="listitem" data-testid={`type-filter-${id}`} />
                ))}
              </Tabs>
            </li>
            <li className={styles.searchHolder}>
              <Search
                className={styles.searchField}
                placeholder="Search Items"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Divider orientation="vertical" size={24} />
              <SortMenu
                iconSize={20}
                onSort={isSearching ? setSearchSort : setSort}
                showName={false}
                showArrow={false}
                sort={menuSort!}
                showRelevanceSort={isSearching}
              />
              <Icon className={styles.icon} type="grid-table" size={20} />
              <Icon className={styles.icon} type="grid-view" size={20} />
            </li>
          </ul>
        )}
      </div>
      {!hasTaggedDocs || filtersNoResult ? (
        renderEmpty
      ) : (
        <div
          ref={ref}
          style={
            {
              '--columns': columns,
            } as CSSProperties
          }
          className={clsx(styles.tagDocumentCards, {
            loading: isLoading,
          })}
          data-testid="tagged-documents"
        >
          {(isLoading ? Array(20).fill(null) : data).map((el: SearchResult | null, index) => {
            const selected = !!taggedDocsMultiSelections?.find((doc) => !!el && doc.id === el?.id);
            return (
              <ListItem
                multiline
                mode="card"
                selected={selected}
                data={el?.document}
                loading={isLoading}
                key={el?.id || index}
                multiselectOn={taggedDocsMultiSelectionsOn}
                appearance={el?.document?.type === 'userSources' ? 'greyscale' : 'default'}
                className={clsx(styles.taggedDocumentCard, {
                  [styles.selected]: selected,
                })}
                menu={getMenuData}
                onClick={(event) => !!el && onListItemClick(event, el)}
              />
            );
          })}
        </div>
      )}
      <OverlaySidebar open={sidebarOverlay?.type === 'document-preview'} onClose={closeSidebarOverlay}>
        <DocumentPreview {...(sidebarOverlay?.context as DocumentPreviewContext)} onClose={closeSidebarOverlay} />
      </OverlaySidebar>
      <MiniActionBar
        withPortal
        on={taggedDocsMultiSelectionsOn}
        data-testid="tagged-documents-action-bar"
        selections={taggedDocsMultiSelections || []}
        onClear={() => {
          setTaggedDocsMultiSelections(null);
          clearLocalRangeSelection();
        }}
        onRemoveFromTag={async () => {
          await onRemoveFromTag(taggedDocsMultiSelections as SearchableDocument[]);
          clearLocalRangeSelection();
        }}
      />
    </>
  );
});
